import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Radio, Users, CalendarCheck, ShieldOff } from "lucide-react";
import { scheduleSummary } from "../lib/schedule";

const StatusDot = ({ status }) => {
  const map = {
    running: "bg-emerald-400",
    paused: "bg-amber-400",
    draft: "bg-white/30",
    ended: "bg-zinc-500",
  };
  return (
    <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/60">
      <span className={`w-1.5 h-1.5 rounded-full ${map[status] || "bg-white/30"} ${status === "running" ? "animate-pulse" : ""}`} />
      {status}
    </span>
  );
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/campaigns");
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="campaigns-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
            Outbound
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-white">Campaigns</h1>
          <p className="text-sm text-white/50 mt-2">
            Upload prospects, scrub DNC, and launch Ashley.
          </p>
        </div>
        <Link
          data-testid="new-campaign-btn"
          to="/campaigns/new"
          className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2 rounded-md transition-colors shadow-[0_0_18px_rgba(255,176,0,0.15)] flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Campaign
        </Link>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-dashed border-white/10 rounded-xl p-16 text-center" data-testid="campaigns-empty-state">
          <Radio className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-white font-medium text-lg mb-2">No campaigns yet</h3>
          <p className="text-white/50 text-sm mb-6">
            Create your first campaign, upload a prospect CSV, and let Ashley start dialing.
          </p>
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-2 bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Campaign
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="campaigns-grid">
          {items.map((c) => {
            const pct = c.stats?.total
              ? Math.round((c.stats.called / c.stats.total) * 100)
              : 0;
            return (
              <Link
                key={c.id}
                to={`/campaigns/${c.id}`}
                data-testid={`campaign-card-${c.id}`}
                className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-[#FFB000]/30 transition-colors group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-white font-medium text-base tracking-tight group-hover:text-[#FFB000] transition-colors">
                      {c.name}
                    </div>
                    <div className="text-[11px] font-mono text-white/40 mt-1">
                      {c.timezone} · {scheduleSummary(c.schedule)} · max {c.max_attempts} attempts
                    </div>
                  </div>
                  <StatusDot status={c.status} />
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  <div>
                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40">
                      <Users className="w-3 h-3" /> Total
                    </div>
                    <div className="font-mono text-xl text-white mt-1">{c.stats?.total ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                      Called
                    </div>
                    <div className="font-mono text-xl text-white mt-1">{c.stats?.called ?? 0}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-500/70">
                      <CalendarCheck className="w-3 h-3" /> Booked
                    </div>
                    <div className="font-mono text-xl text-emerald-400 mt-1">{c.stats?.booked ?? 0}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-red-500/70">
                      <ShieldOff className="w-3 h-3" /> DNC
                    </div>
                    <div className="font-mono text-xl text-red-500 mt-1">{c.stats?.dnc ?? 0}</div>
                  </div>
                </div>

                <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#FFB000] rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[10px] font-mono text-white/40 mt-1.5 text-right">
                  {pct}% dialed
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
